/**
 * The first-run guide: asked once per person on each device, "beginner or experienced?", and for a
 * beginner, the few things outside Vephus they need before it can do anything for them.
 *
 * The answer is kept in this device's storage under the person's id, so somebody else signing in on
 * the same machine is asked for themselves, and the same person on a new machine is asked again —
 * which is when the list of things to install is most likely to be useful anyway.
 */

import { DOWNLOAD_URL } from './startPath'

export type GuideAnswer = 'beginner' | 'experienced'

export function guideKey(userId: string): string {
  return `devpulse:first-run-guide:${userId}`
}

/**
 * What this person answered on this device, or null if they have not been asked. Storage that
 * cannot be read counts as answered: a guide that came back on every page load would be worse than
 * one that never showed, and it can still be opened from the sidebar.
 */
export function guideAnswer(userId: string): GuideAnswer | null {
  try {
    const value = window.localStorage.getItem(guideKey(userId))
    return value === 'beginner' || value === 'experienced' ? value : null
  } catch {
    return 'experienced'
  }
}

export function rememberGuideAnswer(userId: string, answer: GuideAnswer): void {
  try {
    window.localStorage.setItem(guideKey(userId), answer)
  } catch {
    // Remembering is a nicety: the worst case is being asked again.
  }
}

const OPEN_EVENT = 'devpulse:open-first-run-guide'

/** Opens the guide again from anywhere (the sidebar's "Getting started guide"). */
export function openFirstRunGuide(): void {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT))
}

export function onOpenFirstRunGuide(handler: () => void): () => void {
  window.addEventListener(OPEN_EVENT, handler)
  return () => window.removeEventListener(OPEN_EVENT, handler)
}

export type GuideItemId = 'github' | 'claude' | 'docker' | 'desktop' | 'ai'

/**
 * What a beginner wants (docs/refactor/51, N2): to use AI day to day — videos, documents, sites, with no
 * programming — or to program. Only the second needs GitHub, Git, Node and Docker.
 */
export type GuideTrack = 'use' | 'code'

export interface GuideLink {
  /** i18n key of what the link does, e.g. "Install Git". */
  labelKey: string
  url: string
}

export interface GuideItem {
  id: GuideItemId
  links: GuideLink[]
}

/**
 * What a beginner needs, in the order they need it. The desktop app is left out inside the desktop
 * app: telling somebody to download what they are already using reads as a mistake.
 */
export function guideItems(desktop: boolean, track: GuideTrack = 'code'): GuideItem[] {
  // To use AI: the app, and an AI — the Claude subscription (the app installs Claude Code itself) or the free local one.
  if (track === 'use') {
    const use: GuideItem[] = desktop ? [] : [{ id: 'desktop', links: [{ labelKey: 'guide.link.desktop', url: DOWNLOAD_URL }] }]
    use.push({ id: 'ai', links: [{ labelKey: 'guide.link.claudePlan', url: 'https://claude.ai/upgrade' }] })
    return use
  }
  const items: GuideItem[] = [
    {
      id: 'github',
      links: [
        { labelKey: 'guide.link.githubSignup', url: 'https://github.com/signup' },
        { labelKey: 'guide.link.git', url: 'https://git-scm.com/downloads' },
      ],
    },
    {
      id: 'claude',
      links: [
        { labelKey: 'guide.link.claudePlan', url: 'https://claude.ai/upgrade' },
        { labelKey: 'guide.link.claudeCode', url: 'https://docs.anthropic.com/en/docs/claude-code/overview' },
        { labelKey: 'guide.link.node', url: 'https://nodejs.org/' },
      ],
    },
    {
      id: 'docker',
      links: [{ labelKey: 'guide.link.docker', url: 'https://www.docker.com/products/docker-desktop/' }],
    },
  ]
  if (!desktop) {
    items.push({ id: 'desktop', links: [{ labelKey: 'guide.link.desktop', url: DOWNLOAD_URL }] })
  }
  return items
}

/**
 * Where "Start the setup" goes: Conectar agente, on the recommended way — the desktop app — in a browser too.
 * Connecting from an IDE without the app is the advanced tab (docs/refactor/51, N1).
 */
export function setupPath(_desktop: boolean): string {
  return '/agents/connect?tab=desktop'
}
